'use client';

import { useMemo } from 'react';
import { omit } from 'radash';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { useSignalCounts, type SignalCountsFilters } from './useSignalCounts';

type UseSignalsLifecycleViewCountsParams = {
  targetId?: string;
  targetType?: TargetTypeType;
  endpointId?: string;
  surfaceAssetEntityId?: string;
  surfaceAssetKind?: string;
  includeApplicationLevelGroup?: boolean;
  filters?: SignalCountsFilters;
  enabled?: boolean;
};

export function useSignalsLifecycleViewCounts({
  targetId,
  targetType,
  endpointId,
  surfaceAssetEntityId,
  surfaceAssetKind,
  includeApplicationLevelGroup,
  filters,
  enabled = true,
}: UseSignalsLifecycleViewCountsParams) {
  const baseFilters = useMemo(() => {
    if (!filters) {
      return undefined;
    }

    return omit(filters, ['lifecycleState']);
  }, [filters]);
  const queryBase = {
    targetId,
    targetType,
    endpointId,
    surfaceAssetEntityId,
    surfaceAssetKind,
    includeApplicationLevelGroup,
    enabled,
  };
  const { data: allCounts, isLoading: isLoadingAll } = useSignalCounts({
    ...queryBase,
    filters: baseFilters
      ? { ...baseFilters, lifecycleState: 'all' }
      : undefined,
  });
  const { data: activeCounts, isLoading: isLoadingActive } = useSignalCounts({
    ...queryBase,
    filters: baseFilters
      ? { ...baseFilters, lifecycleState: 'active' }
      : undefined,
  });
  const { data: archivedCounts, isLoading: isLoadingArchived } =
    useSignalCounts({
      ...queryBase,
      filters: baseFilters
        ? { ...baseFilters, lifecycleState: 'archived' }
        : undefined,
    });
  const lifecycleCounts = useMemo(() => {
    if (isLoadingAll || isLoadingActive || isLoadingArchived) {
      return undefined;
    }

    if (!allCounts && !activeCounts && !archivedCounts) {
      return undefined;
    }

    return {
      all: allCounts?.counts.ALL ?? 0,
      active: activeCounts?.counts.ALL ?? 0,
      archived: archivedCounts?.counts.ALL ?? 0,
    };
  }, [
    activeCounts,
    allCounts,
    archivedCounts,
    isLoadingActive,
    isLoadingAll,
    isLoadingArchived,
  ]);

  return { lifecycleCounts };
}
